import ApiClient from '@/api/ApiClient';

export type AiReportStatus = 'notGenerated' | 'draft' | 'published';
export type AiReportBatchStatus = 'processing' | 'completed' | 'failed';
export type AiReportBatchKind = 'bulkGenerate' | 'individualRegenerate' | 'bulkPublish';
export type AiReportQuarter = 1 | 2 | 3 | 4;

export const AI_REPORT_LANGUAGE_CODES = [
  'en',
  'ja',
  'ko',
  'zh',
  'vi',
  'km',
  'hi',
  'ru',
  'bn',
  'mn',
  'tl',
  'ms',
] as const;

export type AiReportLanguage = typeof AI_REPORT_LANGUAGE_CODES[number];

export interface AiReportBatchResponse {
  batchId: number;
  nurseryId: number;
  businessYear: number;
  quarter: AiReportQuarter;
  kind: AiReportBatchKind;
  status: AiReportBatchStatus;
  totalCount: number;
  successCount: number;
  failedCount: number;
  startedAt: string;
  finishedAt: string | null;
  triggeredBy: { userId: number; displayName: string } | null;
}

export interface AiReportQuarterSummary {
  quarter: AiReportQuarter;
  monthsLabel: string;
  totalChildren: number;
  notGeneratedCount: number;
  draftCount: number;
  publishedCount: number;
  latestBatch: AiReportBatchResponse | null;
}

export interface AiReportTopResponse {
  businessYear: number;
  quarters: AiReportQuarterSummary[];
}

export interface AiReportChildRow {
  childId: number;
  lastName: string;
  firstName: string;
  className: string;
  ageLabel: string;
  status: AiReportStatus;
  generationCount: number;
  dataCoverageMonths: number[];
  achievedItemCount: number;
  progressRecordCount: number;
  lastGeneratedAt: string | null;
  lastGeneratedBy: string | null;
  lastPublishedAt: string | null;
  lastPublishedBy: string | null;
}

export interface AiReportClassGroup {
  classId: number;
  className: string;
  ageLabel: string;
  children: AiReportChildRow[];
}

export interface AiReportQuarterDetailResponse {
  businessYear: number;
  quarter: AiReportQuarter;
  monthsLabel: string;
  classes: AiReportClassGroup[];
  latestBatch: AiReportBatchResponse | null;
}

export interface AiReportDetailResponse {
  childId: number;
  lastName: string;
  firstName: string;
  className: string;
  ageLabel: string;
  businessYear: number;
  quarter: AiReportQuarter;
  monthsLabel: string;
  status: AiReportStatus;
  content: string | null;
  additionalPrompt: string | null;
  dataCoverageMonths: number[];
  generationCount: number;
  lastGeneratedAt: string | null;
  lastGeneratedBy: string | null;
  lastPublishedAt: string | null;
  lastPublishedBy: string | null;
}

export default class ApiAiReport {
  static async getTop({
    nurseryId,
    businessYear,
  }: {
    nurseryId: number;
    businessYear: number;
  }): Promise<AiReportTopResponse> {
    const response = await ApiClient.get(
      `/child-development/ai-report/${nurseryId}/${businessYear}/summary`,
      null,
    );
    return response as AiReportTopResponse;
  }

  static async getQuarterDetail({
    nurseryId,
    businessYear,
    quarter,
  }: {
    nurseryId: number;
    businessYear: number;
    quarter: AiReportQuarter;
  }): Promise<AiReportQuarterDetailResponse> {
    const response = await ApiClient.get(
      `/child-development/ai-report/${nurseryId}/${businessYear}/quarters/${quarter}`,
      null,
    );
    return response as AiReportQuarterDetailResponse;
  }

  static async getDetail({
    nurseryId,
    businessYear,
    quarter,
    childId,
  }: {
    nurseryId: number;
    businessYear: number;
    quarter: AiReportQuarter;
    childId: number;
  }): Promise<AiReportDetailResponse | null> {
    const response = await ApiClient.get(
      `/child-development/ai-report/${nurseryId}/${businessYear}/quarters/${quarter}/children/${childId}`,
      null,
    );
    return response as AiReportDetailResponse;
  }

  static async regenerateChild({
    nurseryId,
    businessYear,
    quarter,
    childId,
    language,
  }: {
    nurseryId: number;
    businessYear: number;
    quarter: AiReportQuarter;
    childId: number;
    language?: AiReportLanguage | null;
  }): Promise<AiReportBatchResponse> {
    const response = await ApiClient.post(
      `/child-development/ai-report/${nurseryId}/${businessYear}/quarters/${quarter}/children/${childId}/regenerate`,
      { language: language ?? null },
    );
    return response as AiReportBatchResponse;
  }

  static async setStatus({
    nurseryId,
    businessYear,
    quarter,
    childId,
    status,
  }: {
    nurseryId: number;
    businessYear: number;
    quarter: AiReportQuarter;
    childId: number;
    status: 'published' | 'draft';
  }): Promise<AiReportDetailResponse | null> {
    const response = await ApiClient.put(
      `/child-development/ai-report/${nurseryId}/${businessYear}/quarters/${quarter}/children/${childId}/status`,
      { status },
    );
    return response as AiReportDetailResponse;
  }

  static async getBatch({
    nurseryId,
    batchId,
  }: {
    nurseryId: number;
    batchId: number;
  }): Promise<AiReportBatchResponse> {
    const response = await ApiClient.get(
      `/child-development/ai-report/${nurseryId}/batches/${batchId}`,
      null,
    );
    return response as AiReportBatchResponse;
  }
}
